import { colorMatrix } from "../../../maps/components/MapComponent/constants";
import { getCircleColor } from "../../../shared/layout/Plots/utils";

export interface SeedColorProduct {
  id: string;
  name: string;
  options?: number[];
  range?: { min: number; max: number };
}

const validRates = (rates: Array<number | null | undefined>): number[] => [
  ...rates.filter(
    (rate): rate is number =>
      typeof rate === "number" && Number.isFinite(rate) && rate > 0
  ),
];

/** The stable mid-tone used to identify a seed product's legacy color family. */
export const getSeedProductFamilyColor = (productIndex: number): string =>
  colorMatrix[
    ((productIndex % colorMatrix.length) + colorMatrix.length) %
      colorMatrix.length
  ]?.[50] ?? "#0092e4";

/**
 * Derive a display color without changing the saved assignment color. Rates are
 * scaled with the legacy helper using only the actual rates for this plot, so
 * the product keeps its palette family while higher rates become darker and
 * lower rates become lighter. Configured options and product ranges do not
 * affect the shade domain.
 */
export const getSeedProductColor = <T extends SeedColorProduct>(
  product: T,
  products: T[],
  rate: number | null | undefined,
  plotRates: Array<number | null | undefined> = []
): string => {
  const productIndex = products.findIndex((item) => item.id === product.id);
  const familyColor = getSeedProductFamilyColor(Math.max(0, productIndex));

  if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
    return familyColor;
  }

  const rates = validRates(plotRates);
  if (rates.length === 0) return familyColor;

  const trialProducts = products.map((item) => ({ variety: item.name }));
  const rateRows = rates.map((itemRate) => ({
    variety: product.name,
    rate: itemRate,
  }));

  return (
    getCircleColor(product.name, rate, trialProducts, undefined, rateRows) ||
    familyColor
  );
};
